"use client"

import { useState } from "react"
import { Wrench, Loader2, CheckCircle2, ChevronRight, ChevronDown, CircleHelp } from "lucide-react"
import type { ToolRenderProps } from "@/hooks/useToolRenderer"

export function ToolCallDisplay({ name, args, status, result }: ToolRenderProps) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="my-1.5 text-sm">
      {/* Quiet status chip: chrome stays on the neutral tokens, the single
          coral accent marks in-flight work, and completion settles back to
          muted rather than shouting green. */}
      <button
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        className="flex w-full items-center gap-1.5 rounded-md border border-border/60 bg-card/60 px-2.5 py-1.5 text-left transition-colors duration-150 hover:border-border hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-coral/50"
      >
        {expanded ? (
          <ChevronDown size={12} className="text-muted-foreground" />
        ) : (
          <ChevronRight size={12} className="text-muted-foreground" />
        )}
        <Wrench size={12} className="text-muted-foreground" />
        <span className="text-brand-ink-soft">{name}</span>
        {(status === "streaming" || status === "executing") && (
          <Loader2
            size={12}
            className="ml-auto animate-spin text-brand-coral"
            aria-label="Running"
          />
        )}
        {status === "complete" && (
          <CheckCircle2
            size={12}
            className="ml-auto text-muted-foreground"
            aria-label="Completed"
          />
        )}
        {/* Replay-only: the transcript recorded this call but never its
            result, so show a neutral marker instead of implying success. */}
        {status === "unknown" && (
          <CircleHelp
            size={12}
            className="ml-auto text-muted-foreground"
            aria-label="No result was recorded for this call"
          >
            <title>No result was recorded for this call</title>
          </CircleHelp>
        )}
      </button>

      {expanded && (
        <div className="ml-6 mt-1.5 space-y-2 border-l-2 border-border pl-3">
          {args && (
            <div>
              <div className="label-mono">Input</div>
              <pre className="mt-0.5 whitespace-pre-wrap break-words text-xs text-muted-foreground">
                {args}
              </pre>
            </div>
          )}
          {result && (
            <div>
              <div className="label-mono">Result</div>
              <pre className="mt-0.5 whitespace-pre-wrap break-words text-xs text-muted-foreground">
                {result}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
